import { useEffect, useMemo, useState } from "react";
import { getInventarioHistorial } from "../../../services/admin/inventario";

const PAGE_SIZE = 10;

const toneByTipo = {
  CARGA: "info",
  VENTA: "success",
  AJUSTE: "warn",
};

function InventoryHistoryModal({ isOpen, product, onClose }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [page, setPage] = useState(1);

  const subtitle = useMemo(() => {
    if (!product) return "";
    const part = product.no_parte ? ` · Part No. ${product.no_parte}` : "";
    return `${product.nombre}${part}`;
  }, [product]);

  const getNiceErrorMessage = (error, fallback = "Error loading inventory history.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 240 ? clean.slice(0, 240) + "…" : clean;
  };

  const safeDate = (dt) => {
    if (!dt) return "—";
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  useEffect(() => {
    if (!isOpen || !product) return;

    setHistorial([]);
    setAlert(null);
    setLoading(false);
    setPage(1);
  }, [isOpen, product?.producto_id]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !product) return;

    const loadHistory = async () => {
      try {
        setLoading(true);
        setAlert(null);

        const { items: data } = await getInventarioHistorial(product.producto_id);
        setHistorial(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error loading inventory history:", err);
        setAlert({ type: "error", message: getNiceErrorMessage(err) });
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [isOpen, product?.producto_id]);

  const totalItems = historial.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const paginatedHistorial = historial.slice(start, start + PAGE_SIZE);

  if (!isOpen || !product) return null;

  const closeIfBackdrop = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  return (
    <div className="adc-modal-backdrop" onMouseDown={closeIfBackdrop}>
      <div
        className="adc-modal"
        style={{ maxWidth: 900 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="adc-modal-title">Inventory History</h3>
        <p className="adc-modal-subtitle">{subtitle}</p>

        {alert && (
          <div className={`adc-alert adc-alert--${alert.type}`}>
            <span>{alert.message}</span>
            <button
              type="button"
              className="adc-alert-x"
              onClick={() => setAlert(null)}
              disabled={loading}
              aria-label="Close alert"
            >
              ✕
            </button>
          </div>
        )}

        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table className="modern-table">
            <thead>
              <tr>
                <th style={{ position: "sticky", top: 0, zIndex: 5, background: "#F1F5F9" }}>
                  Date
                </th>
                <th style={{ position: "sticky", top: 0, zIndex: 5, background: "#F1F5F9" }}>
                  Movement
                </th>
                <th style={{ position: "sticky", top: 0, zIndex: 5, background: "#F1F5F9" }}>
                  Quantity
                </th>
                <th style={{ position: "sticky", top: 0, zIndex: 5, background: "#F1F5F9" }}>
                  User
                </th>
                <th style={{ position: "sticky", top: 0, zIndex: 5, background: "#F1F5F9" }}>
                  Note
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    Loading history...
                  </td>
                </tr>
              ) : historial.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    No inventory movements
                  </td>
                </tr>
              ) : (
                paginatedHistorial.map((h) => {
                  const tipo = String(h.tipo_movimiento || "").toUpperCase();
                  const tone = toneByTipo[tipo] || "neutral";

                  const user =
                    (h.usuario_nombre || h.usuario_apellido)
                      ? `${h.usuario_nombre || ""} ${h.usuario_apellido || ""}`.trim()
                      : "No user";

                  return (
                    <tr key={h.historial_id}>
                      <td>{safeDate(h.fecha_creado)}</td>

                      <td>
                        <span className={`adc-badge adc-badge--${tone}`}>
                          {tipo || "—"}
                        </span>
                      </td>

                      <td>{h.cantidad}</td>
                      <td>{user}</td>
                      <td style={{ whiteSpace: "pre-wrap" }}>{h.observacion || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && historial.length > PAGE_SIZE && (
          <div className="adc-pagination">
            <span className="adc-pagination-info">
              {start + 1}–{Math.min(start + PAGE_SIZE, totalItems)} of {totalItems}
            </span>
            <div className="adc-pagination-btns">
              <button
                type="button"
                className="adc-pagination-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="adc-pagination-page">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="adc-pagination-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        )}

        <div className="adc-modal-actions">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Close
          </button>
        </div>
        <style>{`
          .adc-alert{
            margin: 12px 0 0;
            padding: 10px 12px;
            border-radius: 10px;
            border: 1px solid;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap: 12px;
            font-weight: 700;
            font-size: 13px;
          }
          .adc-alert--error{
            background:#FEF2F2;
            border-color:#FECACA;
            color:#7F1D1D;
          }
          .adc-alert--info{
            background:#EFF6FF;
            border-color:#BFDBFE;
            color:#1E3A8A;
          }
          .adc-alert-x{
            border:none;
            background:transparent;
            cursor:pointer;
            font-size: 16px;
            opacity:.7;
          }
          .adc-alert-x:hover{ opacity: 1; }

          .adc-badge{
            display:inline-flex;
            align-items:center;
            padding: 6px 10px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 800;
            letter-spacing: .2px;
            border: 1px solid;
          }
          .adc-badge--info{ background:#DBEAFE; border-color:#BFDBFE; color:#1D4ED8; }
          .adc-badge--success{ background:#DCFCE7; border-color:#A7F3D0; color:#15803D; }
          .adc-badge--warn{ background:#FFEDD5; border-color:#FED7AA; color:#C2410C; }
          .adc-badge--neutral{ background:#F1F5F9; border-color:#E2E8F0; color:#334155; }

          .adc-pagination{
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
            margin-top: 12px;
            padding: 10px 0;
            border-top: 1px solid #E2E8F0;
          }
          .adc-pagination-info{
            font-size: 13px;
            color: #64748B;
          }
          .adc-pagination-btns{
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .adc-pagination-page{
            font-size: 13px;
            font-weight: 600;
            color: #334155;
          }
          .adc-pagination-btn{
            min-width: 36px;
            height: 36px;
            padding: 0 10px;
            border: 1px solid #CBD5E1;
            border-radius: 8px;
            background: #F8FAFC;
            font-size: 18px;
            cursor: pointer;
            color: #334155;
            line-height: 1;
          }
          .adc-pagination-btn:hover:not(:disabled){
            background: #F1F5F9;
            border-color: #94A3B8;
          }
          .adc-pagination-btn:disabled{
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    </div>
  );
}

export default InventoryHistoryModal;
